(() => {
  'use strict';

  if (window.MerlonsHomeMedia) return;

  const viewportMargin = () => `${Math.max(240, Math.min(720, Math.round(window.innerHeight || 480)))}px 0px`;
  const mediaQuery = (query) => typeof window.matchMedia === 'function'
    ? window.matchMedia(query)
    : { matches: false, addEventListener() {}, removeEventListener() {} };
  const reducedMotion = mediaQuery('(prefers-reduced-motion: reduce)');
  const shouldConserveData = () => navigator.connection?.saveData === true;
  const inViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return !element.hidden && rect.bottom > 0 && rect.top < window.innerHeight;
  };
  const dataSources = (video) => Array.from(video.querySelectorAll('source[data-src]'));
  const hydrateSources = (video) => {
    dataSources(video).forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.load();
  };

  const observeVideo = ({ video, fallback, playButton, decorative, cleanups, isDestroyed }) => {
    const sourceElements = Array.from(video.querySelectorAll('source'));
    // An editor remount can reuse sources hydrated by the previous controller.
    let loaded = sourceElements.some((source) => Boolean(source.src));
    let failed = false;
    let queued = false;
    let sourceFailures = 0;

    const policyBlocksAutoLoad = () => reducedMotion.matches || shouldConserveData();
    const showFallback = () => {
      video.pause();
      video.hidden = true;
      if (fallback) fallback.hidden = false;
      if (playButton) playButton.hidden = false;
    };
    const load = (explicit = false) => {
      if (isDestroyed() || failed || loaded) return loaded;
      if (!explicit && policyBlocksAutoLoad()) return false;
      loaded = true;
      hydrateSources(video);
      return true;
    };
    const play = (explicit = false) => {
      if (isDestroyed() || !loaded || document.visibilityState === 'hidden' || !inViewport(video) || (!explicit && policyBlocksAutoLoad())) {
        video.pause();
        return;
      }
      const result = video.play();
      if (result?.catch) {
        result.catch(() => {
          if (!decorative && playButton) playButton.hidden = false;
        });
      }
    };
    const update = () => {
      if (document.visibilityState === 'hidden' || !inViewport(video)) {
        video.pause();
        return;
      }
      if (load()) play();
    };
    const handleError = () => {
      if (!loaded || failed || isDestroyed()) return;
      failed = true;
      showFallback();
    };
    const onSourceError = () => {
      if (!loaded || isDestroyed()) return;
      sourceFailures += 1;
      if (sourceFailures >= Math.max(1, sourceElements.length)) handleError();
    };
    const onPlay = () => { if (playButton) playButton.hidden = true; };
    const retry = () => {
      if (failed) {
        failed = false;
        loaded = false;
        sourceFailures = 0;
        video.hidden = false;
        if (fallback) fallback.hidden = true;
      }
      if (load(true)) play(true);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        update();
      });
    };

    video.addEventListener('error', handleError);
    sourceElements.forEach((source) => source.addEventListener('error', onSourceError));
    video.addEventListener('play', onPlay);
    playButton?.addEventListener('click', retry);
    if (!decorative && policyBlocksAutoLoad() && playButton) playButton.hidden = false;

    let preloadObserver;
    let visibilityObserver;
    let usingScrollFallback = false;
    const useScrollFallback = () => {
      if (usingScrollFallback) return;
      usingScrollFallback = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    };
    if (typeof window.IntersectionObserver === 'function') {
      try {
        const options = { rootMargin: viewportMargin() };
        preloadObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            load();
            preloadObserver.unobserve(entry.target);
          });
        }, options);
        visibilityObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) update();
            else entry.target.pause();
          });
        });
        preloadObserver.observe(video);
        visibilityObserver.observe(video);
      } catch (error) {
        preloadObserver?.disconnect();
        visibilityObserver?.disconnect();
        preloadObserver = undefined;
        visibilityObserver = undefined;
        useScrollFallback();
      }
    } else {
      useScrollFallback();
    }

    cleanups.push(() => {
      preloadObserver?.disconnect();
      visibilityObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      video.removeEventListener('error', handleError);
      sourceElements.forEach((source) => source.removeEventListener('error', onSourceError));
      video.removeEventListener('play', onPlay);
      playButton?.removeEventListener('click', retry);
      video.pause();
    });
    return { update, pause: () => video.pause() };
  };

  const waitForImage = (image) => {
    if (image.complete && image.naturalWidth > 0) return Promise.resolve();
    if (typeof image.decode === 'function') return image.decode().catch(() => {});
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  };

  const mountLoops = (root, cleanups, isDestroyed) => {
    const controllers = [];
    root.querySelectorAll('[data-merlons-technology-loop]').forEach((media) => {
      const slides = Array.from(media.querySelectorAll('[data-merlons-technology-slide]'));
      if (slides.length < 2) return;
      const card = media.closest('.merlons-technologies__card') || media;
      let active = 0;
      let timer = 0;
      let paused = false;
      const clear = () => { if (timer) { window.clearInterval(timer); timer = 0; } };
      const setActive = (next) => {
        slides[active]?.classList.remove('is-active');
        active = (next + slides.length) % slides.length;
        slides[active]?.classList.add('is-active');
      };
      const start = () => {
        clear();
        if (paused || reducedMotion.matches || isDestroyed() || document.visibilityState === 'hidden') return;
        timer = window.setInterval(() => setActive(active + 1), 5600);
      };
      const pause = () => { paused = true; clear(); };
      const resume = () => { paused = false; start(); };
      const onMotion = () => {
        if (reducedMotion.matches) { clear(); setActive(0); }
        else start();
      };
      slides.forEach((slide, index) => slide.classList.toggle('is-active', index === 0));
      card.addEventListener('pointerenter', pause);
      card.addEventListener('pointerleave', resume);
      card.addEventListener('focusin', pause);
      card.addEventListener('focusout', resume);
      reducedMotion.addEventListener?.('change', onMotion);
      Promise.all(slides.map(waitForImage)).then(start);
      controllers.push({ pause: clear, resume: start });
      cleanups.push(() => {
        clear();
        card.removeEventListener('pointerenter', pause);
        card.removeEventListener('pointerleave', resume);
        card.removeEventListener('focusin', pause);
        card.removeEventListener('focusout', resume);
        reducedMotion.removeEventListener?.('change', onMotion);
      });
    });
    return controllers;
  };

  const mount = (root, kind) => {
    if (!root || root.dataset.merlonsHomeMediaReady === 'true') return;
    root.dataset.merlonsHomeMediaReady = 'true';
    let destroyed = false;
    const cleanups = [];
    const videos = Array.from(root.querySelectorAll(kind === 'inspiration'
      ? '[data-merlons-inspiration-video]'
      : '[data-merlons-technology-video]'));
    const controllers = videos.map((video) => observeVideo({
      video,
      fallback: video.parentElement?.querySelector(kind === 'inspiration'
        ? '[data-merlons-inspiration-video-fallback]'
        : '[data-merlons-technology-video-fallback]'),
      playButton: kind === 'technology' ? video.parentElement?.querySelector('[data-merlons-technology-play]') : null,
      decorative: kind === 'inspiration',
      cleanups,
      isDestroyed: () => destroyed,
    }));
    const loopControllers = kind === 'technology' ? mountLoops(root, cleanups, () => destroyed) : [];
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        controllers.forEach((controller) => controller.pause());
        loopControllers.forEach((controller) => controller.pause());
      } else {
        controllers.forEach((controller) => controller.update());
        loopControllers.forEach((controller) => controller.resume());
      }
    };
    const onMotion = () => {
      if (reducedMotion.matches) controllers.forEach((controller) => controller.pause());
      else controllers.forEach((controller) => controller.update());
    };
    document.addEventListener('visibilitychange', onVisibility);
    reducedMotion.addEventListener?.('change', onMotion);
    navigator.connection?.addEventListener?.('change', onMotion);
    root.merlonsHomeMediaCleanup = () => {
      if (destroyed) return;
      destroyed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      reducedMotion.removeEventListener?.('change', onMotion);
      navigator.connection?.removeEventListener?.('change', onMotion);
      cleanups.forEach((cleanup) => cleanup());
      delete root.merlonsHomeMediaCleanup;
      delete root.dataset.merlonsHomeMediaReady;
    };
  };

  const findRoots = (target, selector) => target?.matches?.(selector)
    ? [target]
    : Array.from(target?.querySelectorAll?.(selector) || []);
  const api = {
    mountInspiration: (root) => mount(root, 'inspiration'),
    mountTechnologies: (root) => mount(root, 'technology'),
  };
  window.MerlonsHomeMedia = api;
  document.addEventListener('shopify:section:load', (event) => {
    findRoots(event.target, '[data-merlons-inspiration]').forEach(api.mountInspiration);
    findRoots(event.target, '[data-merlons-technologies]').forEach(api.mountTechnologies);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    findRoots(event.target, '[data-merlons-inspiration], [data-merlons-technologies]').forEach((root) => root.merlonsHomeMediaCleanup?.());
  });
  const mountExisting = () => {
    document.querySelectorAll('[data-merlons-inspiration]').forEach(api.mountInspiration);
    document.querySelectorAll('[data-merlons-technologies]').forEach(api.mountTechnologies);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountExisting, { once: true });
  else mountExisting();
})();
